import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Atom,
  BarChart3,
  BookOpen,
  CheckCircle2,
  Cpu,
  Database,
  Factory,
  FileCheck2,
  FlaskConical,
  Gauge,
  GitBranch,
  Landmark,
  Layers,
  Mail,
  Network,
  Route,
  Scale,
  ShieldCheck,
  Sparkles,
  Thermometer,
  Users,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const REQUEST_PAPER =
  'mailto:contact@salartechnologies.com?subject=Salar%20Graph%20Research%20Strategy%20Request&body=Hello%20SALAR%20Technologies%20LLC%2C%0A%0AI%20would%20like%20to%20request%20the%20public-safe%20Salar%20Graph%20research%20strategy%20(PDF%20and%2For%20editable%20DOCX).%0A%0AName%3A%0AOrganization%3A%0AResearch%20or%20collaboration%20interest%3A%0A%0AThank%20you.';

const CONTACT_VIKRANT =
  'mailto:vikrant@salartechnologies.com?subject=Salar%20Graph%20Research%20Collaboration&body=Hello%20Vikrant%2C%0A%0AI%20would%20like%20to%20discuss%20the%20Salar%20Graph%20research%20initiative.%0A%0AName%3A%0AOrganization%3A%0AArea%20of%20interest%3A%0A%0AThank%20you.';

const graphFamilies = [
  {
    icon: Network,
    title: 'Supply-chain dependency graph',
    nodes: 'Suppliers, fabs, OSATs, materials, logistics routes and components',
    question: 'Where are the hidden shared dependencies and disruption paths?',
  },
  {
    icon: Atom,
    title: 'Material-process-property graph',
    nodes: 'Materials, compositions, recipes, process conditions and measured properties',
    question: 'Which candidates may satisfy performance and manufacturability requirements?',
  },
  {
    icon: Thermometer,
    title: 'Reliability and failure graph',
    nodes: 'Stressors, interfaces, components, failure mechanisms and environments',
    question: 'Why could a candidate fail, and which variables dominate lifetime?',
  },
  {
    icon: FileCheck2,
    title: 'Qualification-evidence graph',
    nodes: 'Requirements, tests, samples, lots, results, waivers and reviewers',
    question: 'What evidence supports release, rejection or additional validation?',
  },
  {
    icon: GitBranch,
    title: 'Experiment and provenance graph',
    nodes: 'Datasets, simulations, models, jobs, analysts, instruments and approvals',
    question: 'Can the result be reproduced, audited and defended?',
  },
];

const workflow = [
  ['1', 'Candidate selection', 'Define a bounded material, package or component candidate set.'],
  ['2', 'Physics constraints', 'Apply thermal, electrical, mechanical, environmental and manufacturability limits.'],
  ['3', 'Leadership simulation', 'Use approved leadership-computing resources for parameter sweeps, graph scaling and uncertainty experiments.'],
  ['4', 'Evidence validation', 'Compare predictions with public, synthetic, historical or partner-approved data.'],
  ['5', 'Dependency analysis', 'Connect candidates to suppliers, materials, processes, geography and qualification evidence.'],
  ['6', 'Confidence scoring', 'Quantify uncertainty, evidence completeness and ranking stability.'],
  ['7', 'Human decision gate', 'Advance, hold or reject a candidate through a traceable accountable review.'],
];

const star = [
  {
    key: 'S',
    label: 'Situation',
    body: 'Critical electronics depend on concentrated materials, opaque lower-tier networks and disconnected qualification evidence while federal AI-for-science capacity is expanding.',
  },
  {
    key: 'T',
    label: 'Task',
    body: 'Demonstrate a reproducible method that connects physical reliability, materials, sourcing, manufacturing stability, provenance and qualification evidence.',
  },
  {
    key: 'A',
    label: 'Action',
    body: 'Build synthetic public-safe benchmarks, validate the environment, run controlled scaling studies, quantify uncertainty and preserve a human decision record.',
  },
  {
    key: 'R',
    label: 'Request',
    body: 'Seek technical guidance, validation partners, future allocation pathways and appropriately structured collaboration — not endorsement or unrestricted data access.',
  },
];

const growthEngine = [
  'Open benchmark',
  'Leadership-computing proof',
  'Reproducible public result',
  'Laboratory or industry validation',
  'Federal or consortium proposal',
  'Larger allocation',
  'Properly scoped pilot',
];

const roadmap = [
  ['0–2 weeks', 'Access and environment setup', 'Confirm project membership, filesystem, scheduler, software environment and a minimal reproducible job.'],
  ['3–6 weeks', 'Synthetic graph MVP', 'Create the first bounded material/component graph, schema, data dictionary and baseline algorithms.'],
  ['7–12 weeks', 'Risk-propagation runs', 'Run controlled disruption, reliability and dependency scenarios; compare algorithmic paths.'],
  ['13–18 weeks', 'Scale and uncertainty', 'Increase graph scale only after validation; test sensitivity, uncertainty and ranking stability.'],
  ['19–24 weeks', 'Proof package', 'Produce reproducibility records, public-safe results, collaboration questions and a next-allocation plan.'],
];

const nationalInterest = [
  ['Microelectronics resilience', 'Improve visibility into materials, components, packaging and lower-tier dependencies.'],
  ['Defense infrastructure reliability', 'Prioritize thermal margin, lifetime, maintainability and failure-risk reduction.'],
  ['AI for measurable science', 'Connect AI and graph analytics to physical constraints, uncertainty and validation.'],
  ['Domestic manufacturing readiness', 'Support source confidence, alternate qualification and process-stability evidence.'],
  ['Reproducible research', 'Preserve model versions, job records, assumptions, evidence and accountable approval.'],
  ['Efficient infrastructure', 'Investigate energy, cooling, materials and lifecycle tradeoffs for high-density systems.'],
];

const GraphField = () => {
  const nodes = [
    [95, 88, 6, '#22d3ee'], [178, 58, 5, '#60a5fa'], [256, 110, 7, '#f59e0b'],
    [338, 70, 5, '#22d3ee'], [430, 118, 7, '#34d399'], [520, 62, 5, '#60a5fa'],
    [615, 112, 8, '#22d3ee'], [700, 66, 5, '#f59e0b'], [788, 118, 7, '#60a5fa'],
    [870, 72, 5, '#34d399'], [960, 120, 7, '#22d3ee'], [1055, 66, 5, '#60a5fa'],
    [1138, 112, 8, '#f59e0b'], [1235, 72, 5, '#22d3ee'], [1328, 118, 7, '#34d399'],
    [145, 205, 7, '#34d399'], [230, 175, 5, '#22d3ee'], [325, 220, 8, '#60a5fa'],
    [420, 184, 5, '#f59e0b'], [510, 228, 7, '#22d3ee'], [608, 185, 5, '#34d399'],
    [706, 226, 8, '#60a5fa'], [804, 184, 5, '#22d3ee'], [900, 228, 7, '#f59e0b'],
    [1000, 185, 5, '#34d399'], [1092, 226, 8, '#22d3ee'], [1192, 182, 5, '#60a5fa'],
    [1288, 225, 7, '#34d399'], [1380, 182, 5, '#f59e0b'],
    [84, 326, 5, '#60a5fa'], [190, 286, 7, '#22d3ee'], [290, 336, 5, '#34d399'],
    [392, 288, 8, '#f59e0b'], [498, 334, 5, '#22d3ee'], [602, 290, 7, '#60a5fa'],
    [710, 337, 5, '#34d399'], [812, 288, 8, '#22d3ee'], [918, 336, 5, '#f59e0b'],
    [1020, 288, 7, '#60a5fa'], [1126, 335, 5, '#34d399'], [1230, 290, 8, '#22d3ee'],
    [1336, 336, 5, '#60a5fa'],
  ];
  const edges = [
    [0,1],[1,2],[2,3],[3,4],[4,5],[5,6],[6,7],[7,8],[8,9],[9,10],[10,11],[11,12],[12,13],[13,14],
    [0,15],[1,16],[2,17],[3,18],[4,19],[5,20],[6,21],[7,22],[8,23],[9,24],[10,25],[11,26],[12,27],[13,28],
    [15,16],[16,17],[17,18],[18,19],[19,20],[20,21],[21,22],[22,23],[23,24],[24,25],[25,26],[26,27],[27,28],
    [15,29],[16,30],[17,31],[18,32],[19,33],[20,34],[21,35],[22,36],[23,37],[24,38],[25,39],[26,40],[27,41],
    [29,30],[30,31],[31,32],[32,33],[33,34],[34,35],[35,36],[36,37],[37,38],[38,39],[39,40],[40,41],
    [2,18],[4,20],[6,22],[8,24],[10,26],[12,28],[17,32],[19,34],[21,36],[23,38],[25,40],
  ];

  return (
    <svg viewBox="0 0 1420 410" className="absolute inset-0 h-full w-full" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      <defs>
        <radialGradient id="sg-glow" cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.22" />
          <stop offset="55%" stopColor="#1d4ed8" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#020617" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sg-edge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.10" />
          <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.10" />
        </linearGradient>
      </defs>
      <rect width="1420" height="410" fill="#071120" />
      <rect width="1420" height="410" fill="url(#sg-glow)" />
      <g stroke="url(#sg-edge)" strokeWidth="1.25">
        {edges.map(([a,b], i) => <line key={i} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} />)}
      </g>
      <g>
        {nodes.map(([cx,cy,r,fill], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r={r + 5} fill={fill} opacity="0.07" />
            <circle cx={cx} cy={cy} r={r} fill={fill} opacity="0.82" />
          </g>
        ))}
      </g>
      <g transform="translate(710 205)">
        <circle r="76" fill="#071120" stroke="#22d3ee" strokeOpacity="0.50" />
        <circle r="58" fill="#0c2340" stroke="#60a5fa" strokeOpacity="0.45" />
        <text textAnchor="middle" y="-7" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700" fill="#ffffff">SALAR</text>
        <text textAnchor="middle" y="17" fontFamily="Inter, Arial, sans-serif" fontSize="11" letterSpacing="3" fill="#67e8f9">GRAPH</text>
      </g>
    </svg>
  );
};

const SectionHeading = ({ eyebrow, title, description }) => (
  <div className="max-w-3xl mb-9">
    <div className="text-cyan-300 text-xs font-bold tracking-[0.22em] uppercase mb-3">{eyebrow}</div>
    <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">{title}</h2>
    {description && <p className="mt-4 text-blue-100/90 text-base md:text-lg leading-relaxed">{description}</p>}
  </div>
);

export default function SalarGraph() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ResearchProject',
    name: 'Salar Graph',
    description: 'A public-safe SALAR Technologies LLC research initiative for AI-assisted reliability, materials discovery, semiconductor resilience and leadership-scale graph analytics.',
    dateCreated: '2026-07-22',
    founder: { '@type': 'Person', name: 'Vikrant Salar' },
    parentOrganization: { '@type': 'Organization', name: 'SALAR Technologies LLC', url: 'https://www.salartechnologies.com' },
    url: 'https://www.salartechnologies.com/salar-graph',
  };

  return (
    <div className="min-h-screen bg-[#071120] text-white" data-testid="salar-graph-page">
      <Helmet>
        <title>Salar Graph | AI-Assisted Reliability & Materials Discovery</title>
        <meta name="description" content="Explore Salar Graph, SALAR Technologies LLC's public-safe research initiative for AI-assisted reliability, materials discovery, semiconductor resilience, and leadership-scale graph analytics." />
        <link rel="canonical" href="https://www.salartechnologies.com/salar-graph" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Salar Graph | AI-Assisted Reliability and Materials Discovery" />
        <meta property="og:description" content="A public-safe research initiative connecting physical reliability, materials, supply dependencies, qualification evidence, uncertainty and accountable human decisions." />
        <meta property="og:url" content="https://www.salartechnologies.com/salar-graph" />
        <meta property="og:image" content="https://www.salartechnologies.com/img/salar_whitepaper_divider.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Salar Graph | SALAR Technologies LLC" />
        <meta name="twitter:description" content="AI-assisted reliability and materials discovery for semiconductor and defense infrastructure." />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="border-b border-cyan-400/20 bg-cyan-400/[0.06]">
        <div className="container mx-auto px-6 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
          <span className="inline-flex items-center gap-2 text-cyan-200 text-xs font-bold tracking-[0.18em] uppercase">
            <Sparkles size={14} /> New public-safe research initiative
          </span>
          <span className="hidden sm:block w-px h-4 bg-cyan-400/30" />
          <Link to="/news-updates" className="text-blue-100 text-sm hover:text-white transition-colors inline-flex items-center gap-1.5">
            Published July 22, 2026 <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <section className="relative overflow-hidden border-b border-blue-700/30 min-h-[600px] flex items-center">
        <GraphField />
        <div className="absolute inset-0 bg-gradient-to-r from-[#071120] via-[#071120]/88 to-[#071120]/48" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071120] via-transparent to-[#071120]/20" />
        <div className="container mx-auto px-6 py-24 relative z-10">
          <div className="max-w-4xl">
            <Badge variant="outline" className="border-cyan-400/50 bg-cyan-400/10 text-cyan-200 tracking-[0.18em] text-[11px] mb-6">
              AI FOR SCIENCE • RELIABILITY • MATERIALS • GRAPH COMPUTING
            </Badge>
            <p className="text-cyan-300 font-bold tracking-[0.22em] uppercase text-sm mb-4">SALAR Technologies LLC presents</p>
            <h1 className="text-5xl md:text-7xl font-extrabold leading-[0.98] tracking-tight mb-6">
              Salar <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-300 to-amber-300">Graph</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold text-white leading-snug max-w-3xl">
              AI-Assisted Reliability and Materials Discovery for Semiconductor and Defense Infrastructure
            </h2>
            <p className="mt-6 text-lg md:text-xl text-blue-100 leading-relaxed max-w-3xl">
              A public-safe research initiative for connecting physical constraints, material and component candidates, leadership-scale simulation, supply dependencies, qualification evidence, uncertainty and accountable human decisions.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-[#071120] font-bold px-7">
                <a href={REQUEST_PAPER}>
                  <BookOpen size={18} className="mr-2" /> Request the research strategy
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-blue-300/50 text-blue-50 hover:bg-blue-500/15 px-7">
                <a href={CONTACT_VIKRANT}>
                  <Mail size={18} className="mr-2" /> Contact Vikrant Salar
                </a>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-blue-200/90">
              <span>Public-safe web summary</span>
              <span>Full PDF and DOCX available by request</span>
              <span>Nonproprietary research posture</span>
              <span>No government endorsement implied</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0a1728] border-b border-blue-800/30">
        <div className="container mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ['5,000', 'Polaris node-hours', Cpu],
              ['1 TB', 'Eagle project storage', Database],
              ['5', 'Interconnected graph families', Network],
              ['Synthetic-first', 'Public-safe data boundary', ShieldCheck],
            ].map(([value,label,Icon]) => (
              <div key={label} className="rounded-2xl border border-cyan-400/20 bg-[#0d2038]/75 p-6">
                <Icon className="text-cyan-300 mb-4" size={26} />
                <div className="text-3xl font-extrabold text-white">{value}</div>
                <div className="mt-1 text-blue-200 text-sm">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6">
          <SectionHeading
            eyebrow="Research position"
            title="A decision-evidence system, not a generic AI dashboard"
            description="Salar Graph is intended to show why a material, component, supplier path or manufacturing option may be viable; what evidence is missing; how uncertainty changes the ranking; and where a human reviewer must decide."
          />
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-3xl border border-cyan-400/25 bg-gradient-to-br from-[#0d2038] to-[#081423] p-7 md:p-9">
              <div className="grid md:grid-cols-2 gap-7">
                {[
                  ['Physics before prediction', 'AI recommendations remain bounded by thermal, electrical, mechanical, environmental and manufacturability constraints.', FlaskConical],
                  ['Evidence before approval', 'Scores retain the data, assumptions, model version, validation status, uncertainty and decision rationale behind them.', FileCheck2],
                  ['Dependencies before substitution', 'An apparently suitable alternate is also checked against shared fabs, materials, packaging, routes, provenance and process stability.', Route],
                  ['Human accountability preserved', 'The system can rank and explain candidates, but formal qualification, release and procurement decisions remain human responsibilities.', Scale],
                ].map(([title,body,Icon]) => (
                  <div key={title} className="flex gap-4">
                    <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0"><Icon size={21} className="text-cyan-300" /></div>
                    <div><h3 className="font-bold text-white text-lg">{title}</h3><p className="mt-2 text-blue-100/85 leading-relaxed text-sm">{body}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-amber-400/25 bg-amber-400/[0.06] p-7">
              <Landmark className="text-amber-300 mb-5" size={30} />
              <h3 className="text-xl font-bold text-white">Controlled public statement</h3>
              <p className="mt-4 text-blue-100/90 leading-relaxed text-sm">
                SALAR Technologies LLC received an Argonne Leadership Computing Facility Director's Discretionary allocation for the <code className="text-cyan-200">salargraph</code> project, providing access to Polaris computing resources and Eagle storage for public-safe research on graph-based reliability and semiconductor supply-chain risk.
              </p>
              <p className="mt-4 text-amber-100 text-xs leading-relaxed border-t border-amber-400/20 pt-4">
                This access does not constitute Argonne, DOE or U.S. government endorsement of SALAR Technologies LLC, its products or its patent portfolio.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a1728] border-y border-blue-800/30">
        <div className="container mx-auto px-6">
          <SectionHeading
            eyebrow="Graph architecture"
            title="Five graph families that connect science to operational reality"
            description="The value is not one giant graph. It is the controlled connection of physical, industrial, reliability, qualification and provenance evidence."
          />
          <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-5">
            {graphFamilies.map(({icon:Icon,title,nodes,question},i) => (
              <article key={title} className="rounded-2xl border border-blue-500/25 bg-[#0d2038]/80 p-6 hover:border-cyan-300/55 transition-colors">
                <div className="flex items-center justify-between mb-5"><Icon size={25} className="text-cyan-300" /><span className="text-[11px] font-bold tracking-[0.18em] text-blue-300">G{i+1}</span></div>
                <h3 className="text-lg font-bold text-white leading-snug">{title}</h3>
                <p className="mt-3 text-blue-200/85 text-sm leading-relaxed">{nodes}</p>
                <p className="mt-5 pt-4 border-t border-blue-500/20 text-cyan-100 text-sm leading-relaxed">{question}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6">
          <SectionHeading
            eyebrow="Focused scientific workflow"
            title="A bounded path from candidate selection to traceable human approval"
            description="The first goal is screening and prioritization. It is not a claim of formal certification, production qualification or laboratory replacement."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {workflow.map(([n,title,body],i) => (
              <div key={n} className={`rounded-2xl p-6 border ${i === 6 ? 'border-amber-400/35 bg-amber-400/[0.07]' : 'border-cyan-400/20 bg-[#0d2038]/70'}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-extrabold ${i === 6 ? 'bg-amber-400 text-[#071120]' : 'bg-cyan-400/15 text-cyan-200 border border-cyan-400/30'}`}>{n}</div>
                <h3 className="mt-5 font-bold text-white text-lg">{title}</h3>
                <p className="mt-2 text-blue-100/85 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
            <div className="rounded-2xl p-6 border border-blue-400/25 bg-gradient-to-br from-blue-500/10 to-cyan-500/5 flex flex-col justify-center">
              <Gauge className="text-cyan-300 mb-4" size={26} />
              <h3 className="font-bold text-white text-lg">Measurable outcome</h3>
              <p className="mt-2 text-blue-100/85 text-sm leading-relaxed">Lifetime, thermal margin, energy use, failure probability, qualification efficiency, scenario throughput, ranking stability or uncertainty calibration.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a1728] border-y border-blue-800/30">
        <div className="container mx-auto px-6">
          <SectionHeading eyebrow="STAR execution frame" title="From the current situation to a disciplined collaboration request" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {star.map((item) => (
              <div key={item.key} className="rounded-2xl border border-blue-500/25 bg-[#0d2038]/80 p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400/20 to-blue-500/20 border border-cyan-400/30 flex items-center justify-center text-2xl font-black text-cyan-200">{item.key}</div>
                <h3 className="mt-5 text-xl font-bold text-white">{item.label}</h3>
                <p className="mt-3 text-blue-100/85 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6">
          <SectionHeading
            eyebrow="Growth engine"
            title="A realistic path from open benchmark to validated capability"
            description="ALCF access is an enabling research resource, not an automatic product, contract or endorsement. Value is created through evidence at each successive gate."
          />
          <div className="rounded-3xl border border-cyan-400/25 bg-[#0d2038]/70 p-6 md:p-8 overflow-x-auto">
            <div className="min-w-[980px] flex items-center">
              {growthEngine.map((step,i) => (
                <React.Fragment key={step}>
                  <div className="w-[132px] min-h-[110px] rounded-xl border border-cyan-400/25 bg-[#071523] p-4 flex flex-col items-center justify-center text-center">
                    <span className="text-[10px] tracking-[0.18em] font-bold text-cyan-300">GATE {i+1}</span>
                    <span className="mt-2 text-sm font-bold text-white leading-snug">{step}</span>
                  </div>
                  {i < growthEngine.length - 1 && <ArrowRight className="mx-3 text-blue-400 shrink-0" size={22} />}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="mt-7 grid md:grid-cols-3 gap-5">
            {[
              ['Research asset', 'Benchmark datasets, graph schemas, scenario libraries, uncertainty methods and reproducibility records.', Database],
              ['Transition asset', 'Scaling evidence, publications, collaboration results, future allocation requests and federal proposal support.', BarChart3],
              ['Commercial asset', 'Properly scoped assessment services, partner pilots, software-enabled workflows and patent-development opportunities.', Factory],
            ].map(([title,body,Icon]) => (
              <div key={title} className="rounded-2xl bg-blue-500/[0.06] border border-blue-500/20 p-6 flex gap-4"><Icon size={24} className="text-cyan-300 shrink-0" /><div><h3 className="font-bold text-white">{title}</h3><p className="mt-2 text-blue-100/80 text-sm leading-relaxed">{body}</p></div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a1728] border-y border-blue-800/30">
        <div className="container mx-auto px-6">
          <SectionHeading eyebrow="24-week action plan" title="Start small, preserve allocation, scale only after validation" />
          <div className="space-y-4 max-w-5xl">
            {roadmap.map(([period,title,body],i) => (
              <div key={period} className="grid md:grid-cols-[150px_1fr] gap-4 rounded-2xl border border-blue-500/20 bg-[#0d2038]/70 p-5 md:p-6">
                <div><span className="inline-flex px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/25 text-cyan-200 text-xs font-bold tracking-wide">{period}</span></div>
                <div><h3 className="font-bold text-white text-lg"><span className="text-blue-400 mr-2">0{i+1}</span>{title}</h3><p className="mt-2 text-blue-100/85 text-sm leading-relaxed">{body}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6">
          <SectionHeading eyebrow="National-interest alignment" title="Where the work can contribute without overclaiming" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {nationalInterest.map(([title,body]) => (
              <div key={title} className="rounded-2xl border border-cyan-400/20 bg-[#0d2038]/65 p-6">
                <div className="flex items-center gap-3"><CheckCircle2 size={20} className="text-cyan-300" /><h3 className="font-bold text-white">{title}</h3></div>
                <p className="mt-3 text-blue-100/85 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0a1728] border-y border-blue-800/30">
        <div className="container mx-auto px-6">
          <SectionHeading
            eyebrow="Agreement, IP and publication boundaries"
            title="Public research and protected company value can coexist"
            description="The governing Argonne user-facility agreements, ALCF policies and any later partner agreement control. This public page intentionally contains no credentials, account details, controlled data or unpublished implementation information."
          />
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.06] p-7">
              <ShieldCheck className="text-emerald-300" size={28} />
              <h3 className="mt-4 text-xl font-bold">Public-safe</h3>
              <ul className="mt-4 space-y-3 text-blue-100/85 text-sm">
                {['High-level research purpose and workflow','Synthetic graph schema and public data sources','Non-sensitive scaling methodology and published summaries','Approved acknowledgement and no-endorsement language'].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-300 shrink-0 mt-0.5" />{x}</li>)}
              </ul>
            </div>
            <div className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] p-7">
              <Layers className="text-amber-300" size={28} />
              <h3 className="mt-4 text-xl font-bold">Controlled within SALAR</h3>
              <ul className="mt-4 space-y-3 text-blue-100/85 text-sm">
                {['Unpublished implementation and source code','Proprietary scoring weights and model architecture','Patent-development and commercialization strategy','Partner-specific data and confidential validation results'].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-amber-300 shrink-0 mt-0.5" />{x}</li>)}
              </ul>
            </div>
            <div className="rounded-2xl border border-red-400/25 bg-red-400/[0.05] p-7">
              <ShieldCheck className="text-red-300" size={28} />
              <h3 className="mt-4 text-xl font-bold">Not part of this public project</h3>
              <ul className="mt-4 space-y-3 text-blue-100/85 text-sm">
                {['Classified information or controlled unclassified information','ITAR/EAR-controlled technical data','Customer-proprietary BOMs or restricted supplier data','Credentials, tokens, access instructions or nonpublic facility information'].map(x => <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-red-300 shrink-0 mt-0.5" />{x}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="rounded-3xl overflow-hidden border border-cyan-400/30 bg-gradient-to-br from-[#0d2b47] via-[#0c2038] to-[#071120] relative">
            <div className="absolute inset-0 opacity-30"><GraphField /></div>
            <div className="relative z-10 p-8 md:p-12 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="text-cyan-300 text-xs font-bold tracking-[0.2em] uppercase">Research strategy access</div>
                <h2 className="mt-3 text-3xl md:text-4xl font-bold">Request the full Salar Graph research strategy</h2>
                <p className="mt-4 max-w-3xl text-blue-100 leading-relaxed">The complete 17-page PDF and editable DOCX are maintained in SALAR's controlled document library. They cover the scientific workflow, graph architecture, STAR framework, growth engine, national-interest alignment, measurable outcomes, collaboration model, roadmap and public/private boundaries.</p>
                <p className="mt-3 max-w-3xl text-blue-200/80 text-sm leading-relaxed">Please include your name, organization and research or collaboration interest. SALAR will provide the appropriate public-safe document directly.</p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                <Button asChild size="lg" className="bg-cyan-400 hover:bg-cyan-300 text-[#071120] font-bold">
                  <a href={REQUEST_PAPER}><BookOpen size={18} className="mr-2" /> Request PDF / DOCX</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-blue-300/50 text-white hover:bg-white/10">
                  <Link to="/contact"><Users size={18} className="mr-2" /> Discuss collaboration</Link>
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center text-xs text-blue-300/75 max-w-5xl mx-auto leading-relaxed">
            Public-safe research positioning only. SALAR Technologies LLC is an independent ALCF user institution. Access to ALCF resources does not imply validation, certification, sponsorship, partnership or endorsement by Argonne National Laboratory, UChicago Argonne, LLC, the U.S. Department of Energy or the U.S. government.
          </div>
        </div>
      </section>
    </div>
  );
}
